import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resources } from "@constants/hardcoded-strings";
import { ORGANIZATION_ACTIVITY_REFERENCE_DATA_TYPES, OrganizationActivityReferenceData } from "@constants/reference-data";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { OrganizationActivitySearchFiltersState } from "@idw-types/state/organization-activity-state";
import { setOrganizationActivitySearchFilters } from "@redux/actions/organization-activity-actions";
import { resetTableSettingsTableState } from "@redux/actions/table-settings-actions";
import { getOrganizationActivityReduxRequest } from "@redux-requests/organization-activity";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { CardContainer } from "@containers/common/card-container/card-container";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ActivityLogTableContainer } from "../common/activity-log-table-container/activity-log-table-container";
import { ActivityLogSearchForm } from "../common/activity-log-search-form/activity-log-search-form";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { getOrganizationActivityState, getTableSettingsState } from "@redux/selectors/root-selectors";

export const OrganizationActivityContainer = () => {
    const dispatch = useDispatch();

    const { organizationActivity: organizationActivityTableState } = useSelector(getTableSettingsState);
    const {
        searchFilters,
        searchResults,
    } = useSelector(getOrganizationActivityState);
    const referenceData = useReferenceDataSelector<OrganizationActivityReferenceData>(ORGANIZATION_ACTIVITY_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.organizationActivity && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                ORGANIZATION_ACTIVITY_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.ORGANIZATION_ACTIVITY
            );
        }
    }, [referenceData.isLoading.organizationActivity, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.documentationLinks
        );
    }, [
        referenceData.axnCompanies,
        referenceData.documentationLinks
    ]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading.organizationActivity;
    }, [referenceData.isLoading.organizationActivity]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData;
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    const handleSearch = async (newSearchFilters: OrganizationActivitySearchFiltersState) => {
        const { companyId, startDate, endDate, timeRangeType } = newSearchFilters;

        dispatch(resetTableSettingsTableState(TableSettingsTableTypes.ORGANIZATION_ACTIVITY));
        dispatch(setOrganizationActivitySearchFilters({
            companyId,
            startDate,
            endDate,
            timeRangeType
        }));

        getOrganizationActivityReduxRequest(dispatch)(
            companyId,
            startDate,
            endDate,
        );
    };

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader
                title={resources.pages.organizationActivity.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"organization-activity-page-header"}
                    />
                }
            />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <ActivityLogSearchForm
                        companies={referenceData.axnCompanies}
                        requestInProgress={searchResults.isLoading}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.activityLogs && (
                    <ActivityLogTableContainer
                        activityLogs={searchResults.activityLogs}
                        tableState={organizationActivityTableState}
                        isPageHeight={true}
                        tableSettingsTableType={TableSettingsTableTypes.ORGANIZATION_ACTIVITY}
                    />
                )}
                {isLoaded && searchResults.isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};